import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Layers, Play, RefreshCw, XCircle, Eye, Loader2, CheckCircle2,
  AlertTriangle, ListChecks, Sparkles, ChevronRight,
} from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import type {
  MaarifBatchPreview, MaarifBatchJob, MaarifBatchStep, MaarifBatchParams,
} from "../../types/maarif-batch";

const STATUS_LABEL: Record<string, string> = {
  PLANNING: "Plan", PENDING: "Sırada", RUNNING: "Üretiliyor",
  COMPLETED: "Tamamlandı", FAILED: "Başarısız", CANCELED: "İptal",
};
const STATUS_COLOR: Record<string, string> = {
  PLANNING: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  RUNNING: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  CANCELED: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

const MixBar: React.FC<{ mix: { Kolay: number; Orta: number; Zor: number } }> = ({ mix }) => {
  const total = mix.Kolay + mix.Orta + mix.Zor || 1;
  const seg = (n: number, color: string, label: string) =>
    n > 0 ? <div className={color} style={{ width: `${(n / total) * 100}%` }} title={`${label}: ${n}`} /> : null;
  return (
    <div className="flex h-2 w-28 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
      {seg(mix.Kolay, "bg-emerald-400", "Kolay")}
      {seg(mix.Orta, "bg-amber-400", "Orta")}
      {seg(mix.Zor, "bg-red-400", "Zor")}
    </div>
  );
};

/** Üst başlık + sekme yapısı (Randevular ile aynı tasarım) */
export default function BatchGeneratePage() {
  const tabs: TabItemProps[] = [
    { type: "custom", title: "Yeni Üretim", path: "new", tabId: "new", customComponent: <BatchNewTab /> },
    { type: "custom", title: "İşler", path: "jobs", tabId: "jobs", customComponent: <BatchJobsTab /> },
  ];
  return <TabPage title="Toplu Soru Üretimi" tabs={tabs} />;
}

/* ───────────────────────── Yeni Üretim sekmesi ───────────────────────── */
function BatchNewTab() {
  const { showToast, showConfirm } = useModal();

  const [courses, setCourses] = useState<Array<{ code: string; name: string }>>([]);
  const [courseCode, setCourseCode] = useState("");
  const [grades, setGrades] = useState<number[]>([]);
  const [grade, setGrade] = useState<number | "">("");
  const [units, setUnits] = useState<any[]>([]);
  const [unitCode, setUnitCode] = useState("");

  const [testSize, setTestSize] = useState(12);
  const [variants, setVariants] = useState(3);
  const [withJudge, setWithJudge] = useState(true);

  const [preview, setPreview] = useState<MaarifBatchPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Test-bazlı üretim: seçili testler (key = "konuKey__testIndex"). Varsayılan: hepsi seçili.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const tkey = (konuKey: string, testIndex: number) => `${konuKey}__${testIndex}`;

  useEffect(() => { ApiService.getCurriculumCourses().then(setCourses).catch(() => setCourses([])); }, []);
  useEffect(() => {
    if (!courseCode) { setGrades([]); setGrade(""); return; }
    ApiService.getCurriculumGrades(courseCode).then((g) => { setGrades(g); setGrade(""); }).catch(() => setGrades([]));
  }, [courseCode]);
  useEffect(() => {
    if (!courseCode || grade === "") { setUnits([]); setUnitCode(""); return; }
    ApiService.getCurriculumUnits(courseCode, Number(grade)).then((u) => { setUnits(u); setUnitCode(""); }).catch(() => setUnits([]));
  }, [courseCode, grade]);
  useEffect(() => { setPreview(null); setPreviewError(null); }, [unitCode, testSize, variants, withJudge]);

  const params: MaarifBatchParams = useMemo(
    () => ({ testSize, variants, withJudge, visualRatio: 0 }), [testSize, variants, withJudge]
  );
  const canBatch = preview?.health?.canBatchGenerate;

  // Test seçimi: tüm key'ler, seçili toplamlar ve toggle yardımcıları
  const allKeys = useMemo(() => {
    const s = new Set<string>();
    preview?.blueprint.konular.forEach((k) => k.tests.forEach((t) => s.add(tkey(k.key, t.testIndex))));
    return s;
  }, [preview]);
  const selectedTotals = useMemo(() => {
    let tests = 0, soru = 0;
    preview?.blueprint.konular.forEach((k) => k.tests.forEach((t) => {
      if (selectedKeys.has(tkey(k.key, t.testIndex))) { tests += 1; soru += t.size; }
    }));
    return { tests, soru };
  }, [preview, selectedKeys]);
  const toggleTest = (konuKey: string, testIndex: number) =>
    setSelectedKeys((prev) => { const n = new Set(prev); const k = tkey(konuKey, testIndex); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleKonu = (kk: string, testIndexes: number[]) =>
    setSelectedKeys((prev) => {
      const n = new Set(prev);
      const keys = testIndexes.map((ti) => tkey(kk, ti));
      const allOn = keys.every((x) => n.has(x));
      keys.forEach((x) => (allOn ? n.delete(x) : n.add(x)));
      return n;
    });
  const toggleAll = () => setSelectedKeys((prev) => (prev.size >= allKeys.size ? new Set() : new Set(allKeys)));

  const doPreview = async () => {
    if (!unitCode) return;
    setPreviewing(true); setPreviewError(null);
    try {
      const p = await ApiService.previewMaarifBatch({ unitCode, params });
      setPreview(p);
      const all = new Set<string>();
      p.blueprint.konular.forEach((k) => k.tests.forEach((t) => all.add(tkey(k.key, t.testIndex))));
      setSelectedKeys(all); // varsayılan: tüm testler seçili
    }
    catch (e: any) { setPreviewError(e?.message || "Plan oluşturulamadı"); setPreview(null); }
    finally { setPreviewing(false); }
  };

  const doCreate = async () => {
    if (!preview) return;
    if (selectedKeys.size === 0) { showToast("En az bir test seçin", "warning"); return; }
    const selectedTests: { konuKey: string; testIndex: number }[] = [];
    preview.blueprint.konular.forEach((k) => k.tests.forEach((t) => {
      if (selectedKeys.has(tkey(k.key, t.testIndex))) selectedTests.push({ konuKey: k.key, testIndex: t.testIndex });
    }));
    const ok = await showConfirm({
      title: "Üretimi başlat",
      message: `${selectedTotals.soru} soru, ${selectedTotals.tests} test üretilecek. Başlatılsın mı?`,
      confirmText: "Başlat", confirmButtonVariant: "primary",
    });
    if (!ok) return;
    setCreating(true);
    try {
      // Tümü seçiliyse selectedTests gönderme (tüm ünite); kısmiyse yalnızca seçilenler
      const payload = selectedKeys.size >= allKeys.size ? { unitCode, params } : { unitCode, params, selectedTests };
      await ApiService.createMaarifBatch(payload);
      showToast("Üretim başlatıldı — 'İşler' sekmesinden takip edebilirsin", "success");
      setPreview(null);
    } catch (e: any) { showToast(e?.message || "Üretim başlatılamadı", "error"); }
    finally { setCreating(false); }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 p-4 md:p-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Ders</span>
            <select className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              value={courseCode} onChange={(e) => setCourseCode(e.target.value)}>
              <option value="">Seçin…</option>
              {courses.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Sınıf</span>
            <select className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              value={grade} disabled={!courseCode} onChange={(e) => setGrade(e.target.value ? Number(e.target.value) : "")}>
              <option value="">Seçin…</option>
              {grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Ünite</span>
            <select className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              value={unitCode} disabled={grade === ""} onChange={(e) => setUnitCode(e.target.value)}>
              <option value="">Seçin…</option>
              {units.map((u) => <option key={u.unitCode} value={u.unitCode}>{u.unitName}</option>)}
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Test boyutu</span>
            <input type="number" min={4} max={40} value={testSize} onChange={(e) => setTestSize(Number(e.target.value) || 12)}
              className="w-24 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Varyant</span>
            <input type="number" min={1} max={5} value={variants} onChange={(e) => setVariants(Number(e.target.value) || 3)}
              className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={withJudge} onChange={(e) => setWithJudge(e.target.checked)} className="h-4 w-4 rounded" />
            Judge denetimi (kaliteli ama yavaş)
          </label>
          <button onClick={doPreview} disabled={!unitCode || previewing}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Planı Önizle
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">Görsel üretimi kapalı (yalnızca metin soruları).</p>
        {previewError && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/30 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" /> {previewError}
          </div>
        )}
      </div>

      {preview && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Plan: {preview.unit.name}</h2>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-violet-600 dark:text-violet-300">{selectedTotals.tests}/{preview.blueprint.totals.testCount} test</span>
              <span>·</span>
              <span className="font-medium text-gray-700 dark:text-gray-200">{selectedTotals.soru} soru seçili</span>
              <button onClick={toggleAll} className="rounded border border-gray-300 px-2 py-0.5 text-[11px] hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
                {selectedKeys.size >= allKeys.size ? "Tümünü kaldır" : "Tümünü seç"}
              </button>
            </div>
          </div>

          {!canBatch && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" /> Ünite sağlığı kritik — üretim önerilmez.
            </div>
          )}

          <div className="space-y-3">
            {preview.blueprint.konular.map((k) => (
              <div key={k.key} className="rounded-xl border border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between px-3 py-2">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                    <input type="checkbox" className="h-4 w-4 rounded"
                      checked={k.tests.every((t) => selectedKeys.has(tkey(k.key, t.testIndex)))}
                      onChange={() => toggleKonu(k.key, k.tests.map((t) => t.testIndex))} />
                    <ChevronRight className="h-4 w-4 text-violet-500" /> {k.label}
                  </label>
                  <span className="text-xs text-gray-400">{k.tests.length} test · {k.totalQuestions} soru</span>
                </div>
                <div className="divide-y divide-gray-50 dark:divide-gray-700/60">
                  {k.tests.map((t) => (
                    <div key={t.testIndex} className={`flex items-center justify-between px-3 py-1.5 text-xs ${selectedKeys.has(tkey(k.key, t.testIndex)) ? "text-gray-600 dark:text-gray-300" : "text-gray-300 dark:text-gray-600"}`}>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input type="checkbox" className="h-3.5 w-3.5 rounded"
                          checked={selectedKeys.has(tkey(k.key, t.testIndex))}
                          onChange={() => toggleTest(k.key, t.testIndex)} />
                        Test {t.testIndex} · {t.size} soru
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums text-gray-400">K{t.difficultyMix.Kolay} O{t.difficultyMix.Orta} Z{t.difficultyMix.Zor}</span>
                        <MixBar mix={t.difficultyMix} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <button onClick={doCreate} disabled={creating || !canBatch || selectedTotals.tests === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Seçili {selectedTotals.tests} Testi Üret
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── İşler sekmesi ───────────────────────── */
function BatchJobsTab() {
  const { showToast, showConfirm } = useModal();
  const [jobs, setJobs] = useState<MaarifBatchJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<MaarifBatchJob | null>(null);
  const [steps, setSteps] = useState<MaarifBatchStep[] | null>(null);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshJobs = async () => {
    try { const res = await ApiService.listMaarifBatchJobs({ limit: 20 }); setJobs(res.data || []); }
    catch { /* yoksay */ }
  };

  useEffect(() => {
    refreshJobs();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const selectJob = (job: MaarifBatchJob) => {
    setSelectedJob(job); setSteps(null);
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const fresh = await ApiService.getMaarifBatchJob(job.id);
        setSelectedJob(fresh);
        setJobs((prev) => prev.map((j) => (j.id === fresh.id ? fresh : j)));
        if (!["PENDING", "RUNNING"].includes(fresh.status) && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } catch { /* yoksay */ }
    };
    if (["PENDING", "RUNNING"].includes(job.status)) pollRef.current = setInterval(tick, 4000);
    tick();
  };

  const doResume = async (job: MaarifBatchJob) => {
    try { const fresh = await ApiService.resumeMaarifBatchJob(job.id); showToast("Devam ettiriliyor", "success"); selectJob(fresh); refreshJobs(); }
    catch (e: any) { showToast(e?.message || "Devam ettirilemedi", "error"); }
  };
  const doCancel = async (job: MaarifBatchJob) => {
    const ok = await showConfirm({ title: "İşi iptal et", message: "Bu iş iptal edilsin mi? Üretilen sorular korunur.", confirmText: "İptal et", confirmButtonVariant: "danger" });
    if (!ok) return;
    try { const fresh = await ApiService.cancelMaarifBatchJob(job.id); showToast("İptal edildi", "success"); selectJob(fresh); refreshJobs(); }
    catch (e: any) { showToast(e?.message || "İptal edilemedi", "error"); }
  };
  const loadSteps = async (job: MaarifBatchJob) => {
    setLoadingSteps(true);
    try { const res = await ApiService.getMaarifBatchJobSteps(job.id, { status: "DONE" }); setSteps(res.data || []); }
    catch { setSteps([]); } finally { setLoadingSteps(false); }
  };

  return (
    <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-4 p-4 md:p-6 lg:grid-cols-3">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100"><ListChecks className="h-4 w-4" /> İşler</h2>
          <button onClick={refreshJobs} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><RefreshCw className="h-4 w-4" /></button>
        </div>
        {jobs.length === 0 && <p className="text-sm text-gray-400">Henüz iş yok.</p>}
        <div className="space-y-2">
          {jobs.map((j) => (
            <button key={j.id} onClick={() => selectJob(j)}
              className={`w-full rounded-xl border p-3 text-left transition ${selectedJob?.id === j.id ? "border-violet-300 bg-violet-50 dark:border-violet-700 dark:bg-violet-900/20" : "border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{j.unitName || j.unitCode}</span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLOR[j.status]}`}>{STATUS_LABEL[j.status]}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div className="h-full bg-violet-500" style={{ width: `${j.progress?.percent ?? Math.round((j.completedSteps / (j.totalSteps || 1)) * 100)}%` }} />
                </div>
                <span className="text-[10px] tabular-nums text-gray-400">{j.completedSteps}/{j.totalSteps}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        {selectedJob ? (
          <JobDetail job={selectedJob} steps={steps} loadingSteps={loadingSteps}
            onResume={() => doResume(selectedJob)} onCancel={() => doCancel(selectedJob)} onLoadSteps={() => loadSteps(selectedJob)} />
        ) : (
          <div className="flex h-full min-h-[200px] items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400 dark:border-gray-700">
            Detay için soldan bir iş seç.
          </div>
        )}
      </div>
    </div>
  );
}

const JobDetail: React.FC<{
  job: MaarifBatchJob; steps: MaarifBatchStep[] | null; loadingSteps: boolean;
  onResume: () => void; onCancel: () => void; onLoadSteps: () => void;
}> = ({ job, steps, loadingSteps, onResume, onCancel, onLoadSteps }) => {
  const p = job.progress;
  const percent = p?.percent ?? Math.round((job.completedSteps / (job.totalSteps || 1)) * 100);
  const active = ["PENDING", "RUNNING"].includes(job.status);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {job.status === "COMPLETED" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            : job.status === "FAILED" ? <AlertTriangle className="h-5 w-5 text-red-500" />
            : active ? <Loader2 className="h-5 w-5 animate-spin text-blue-500" /> : <Layers className="h-5 w-5 text-gray-400" />}
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{job.unitName || job.unitCode}</h2>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLOR[job.status]}`}>{STATUS_LABEL[job.status]}</span>
        </div>
        <div className="flex items-center gap-2">
          {(job.status === "FAILED" || job.status === "CANCELED") && (
            <button onClick={onResume} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              <RefreshCw className="h-3.5 w-3.5" /> Devam Et
            </button>
          )}
          {active && (
            <button onClick={onCancel} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-300">
              <XCircle className="h-3.5 w-3.5" /> İptal
            </button>
          )}
          <button onClick={onLoadSteps} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            <Eye className="h-3.5 w-3.5" /> Üretilenleri Gör
          </button>
        </div>
      </div>

      <div className="mb-1 flex items-center gap-3">
        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div className="h-full bg-violet-500 transition-all" style={{ width: `${percent}%` }} />
        </div>
        <span className="text-xs font-medium tabular-nums text-gray-600 dark:text-gray-300">{percent}%</span>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>Toplam {job.totalSteps}</span>
        <span className="text-emerald-600 dark:text-emerald-400">✓ {p?.done ?? job.completedSteps}</span>
        {(p?.running ?? 0) > 0 && <span className="text-blue-600 dark:text-blue-400">⟳ {p?.running}</span>}
        {(p?.failed ?? job.failedSteps) > 0 && <span className="text-red-600 dark:text-red-400">✗ {p?.failed ?? job.failedSteps}</span>}
        {job.metadata?.createdTests != null && <span>· {job.metadata.createdTests} test oluşturuldu</span>}
      </div>
      {job.status === "COMPLETED" && (
        <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
          Sorular Soru Bankası'na (Dizgici) düştü. Oradan "Dizgici'den aktar" ile derse alabilirsin.
        </p>
      )}

      {steps !== null && (
        <div className="mt-4">
          {loadingSteps ? (
            <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>
          ) : steps.length === 0 ? (
            <p className="text-sm text-gray-400">Henüz üretilmiş soru yok.</p>
          ) : (
            <div className="space-y-3">
              {steps.map((s) => (
                <div key={s.id} className="rounded-xl border border-gray-100 p-3 dark:border-gray-700">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-700">{s.outcomeCode}{s.processLetter ? ` · ${s.processLetter}` : ""}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-700">{s.difficulty}</span>
                    {s.konuLabel && <span>{s.konuLabel} · Test {s.testIndex}</span>}
                    {s.judgeReport?.verdict && <span className={s.judgeReport.verdict === "pass" ? "text-emerald-500" : "text-amber-500"}>judge: {s.judgeReport.verdict}</span>}
                  </div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {String(s.question?.questionStem || s.question?.soru_metni || s.question?.text || "")}
                  </p>
                  <div className="mt-1.5 space-y-0.5">
                    {["A", "B", "C", "D", "E"].map((k) => {
                      const txt = s.question?.choices?.[k];
                      if (!txt) return null;
                      const correct = s.question?.correctAnswer === k;
                      return (
                        <div key={k} className={`text-xs ${correct ? "font-semibold text-emerald-600 dark:text-emerald-400" : "text-gray-600 dark:text-gray-400"}`}>
                          {k}) {String(txt)} {correct && "✓"}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
